// Small JS client for podcast + existing endpoints
// - Uses CONFIG.API_URL as the single source of truth
// - Supports an async token getter registered from the app
import CONFIG from "../config";
import { fetchWithTimeout, readJsonResponse, responseError } from '../apiRequest';
import { withClientVersion } from '../clientVersion';

let tokenGetter = null;
/**
 * Register a sync/async function that returns the current auth token.
 *   fn: () => string|null | Promise<string|null>
 */
export function setTokenGetter(fn) { tokenGetter = fn; }

async function authHeaders() {
  const token = tokenGetter ? await tokenGetter() : null;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const API_BASE = (CONFIG.API_URL || "").replace(/\/$/, "");

export async function podcastResolve(url) {
  return podcastRequest('/podcast/resolve', { url });
}

export async function podcastIngest(episode, preferTranscript) {
  return podcastRequest('/podcast/ingest', { episode, prefer_transcript: !!preferTranscript }, 1200000);
}

async function podcastRequest(path, body, timeout=30000) {
  for (let attempt=0; ; attempt++) {
    const r = await fetchWithTimeout(withClientVersion(`${API_BASE}${path}`), {
      method:'POST', headers:await authHeaders(), body:JSON.stringify(body),
    }, timeout);
    const payload = await readJsonResponse(r);
    if (r.ok) return payload;
    const failure = Object.assign(responseError(r,payload,'Podcast import failed'), { payload,
      operation:path.endsWith('/resolve') ? 'podcast_resolve' : 'podcast_import' });
    const code = payload?.code;
    const rawRetry = r.headers?.get?.('Retry-After') || payload?.retry_after;
    const retrySeconds = rawRetry ? Number.isFinite(Number(rawRetry)) ? Number(rawRetry)
      : (Date.parse(rawRetry)-Date.now())/1000 : 1;
    // An explicit short rate limit rejected this request. Quota limits and
    // ambiguous transport failures must never create a second saved recording.
    const rejectedRateLimit = (r.status===429 && ['source_rate_limited','upload_rate_limited'].includes(code))
      || (r.status===503 && code==='source_rate_limited' && payload?.source_status===429);
    if (rejectedRateLimit
        && Number.isFinite(retrySeconds) && retrySeconds>0 && retrySeconds<=30 && attempt<2) {
      await new Promise(resolve=>setTimeout(resolve,retrySeconds*1000));
      continue;
    }
    throw failure;
  }
}

export async function startTranscription(args) {
  const headers = await authHeaders();
  const r = await fetchWithTimeout(withClientVersion(`${API_BASE}/start`), {
    method: "POST",
    headers,
    body: JSON.stringify(args),
  });
  if (!r.ok) {
    const payload = await readJsonResponse(r);
    throw Object.assign(responseError(r,payload,'Transcription could not be started'),{payload});
  }
  return readJsonResponse(r);
}

export async function getHistory() {
  const headers = await authHeaders();
  const r = await fetch(`${API_BASE}/history`, { method: "GET", headers });
  if (!r.ok) {
    let j = {}; try { j = await r.json(); } catch {}
    throw new Error(j.error || `History failed (${r.status})`);
  }
  return r.json();
}

export async function getStatus(id) {
  const headers = await authHeaders();
  const r = await fetch(`${API_BASE}/status/${id}`, { method: "GET", headers });
  if (!r.ok) {
    let j = {}; try { j = await r.json(); } catch {}
    throw new Error(j.error || `Status failed (${r.status})`);
  }
  return r.json();
}

export async function getResult(id) {
  const headers = await authHeaders();
  const r = await fetch(`${API_BASE}/result/${id}`, { method: "GET", headers });
  if (!r.ok) {
    let j = {}; try { j = await r.json(); } catch {}
    throw new Error(j.error || `Result failed (${r.status})`);
  }
  return r.json();
}
